/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import React from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
export var EuiAspectRatio = ({
  children,
  className,
  height,
  width,
  maxWidth,
  ...rest
}) => {
  const classes = classNames('euiAspectRatio', className);
  const paddingBottom = `${height / width * 100}%`;
  const content = <div className={classes} {...rest} style={{
    paddingBottom: paddingBottom,
    maxWidth: maxWidth ? maxWidth : 'auto'
  }}>
      {children}
    </div>;
  let contentwithoptionalwrap = content;

  if (maxWidth) {
    contentwithoptionalwrap = <div style={{
      maxWidth: maxWidth
    }}>{content}</div>;
  }

  return contentwithoptionalwrap;
};
EuiAspectRatio.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,

  /**
       * Aspect ratio height. For example 9 would be widescreen video.
       */
  height: PropTypes.number.isRequired,

  /**
       * Aspect ratio width. For example 16 would be widescreen video.
       */
  width: PropTypes.number.isRequired,

  /**
       * The maximum width you want the child to stretch to.
       */
  maxWidth: PropTypes.number
};

try {
  EuiAspectRatio.__docgenInfo = {
    description: '',
    displayName: 'EuiAspectRatio',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'app/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'app/src/components/common.ts',
          name: 'CommonProps'
        },
        required: false,
        type: {
          name: 'string'
        }
      },
      height: {
        defaultValue: null,
        description: 'Aspect ratio height. For example 9 would be widescreen video.',
        name: 'height',
        parent: undefined,
        required: true,
        type: {
          name: 'number'
        }
      },
      width: {
        defaultValue: null,
        description: 'Aspect ratio width. For example 16 would be widescreen video.',
        name: 'width',
        parent: undefined,
        required: true,
        type: {
          name: 'number'
        }
      },
      maxWidth: {
        defaultValue: null,
        description: 'The maximum width you want the child to stretch to.',
        name: 'maxWidth',
        parent: undefined,
        required: false,
        type: {
          name: 'number'
        }
      }
    },
    extendedInterfaces: ['HTMLAttributes', 'AriaAttributes', 'DOMAttributes', 'CommonProps']
  };
} catch (e) {}